import {
    isLoadingValue,
    makeLoadingValueWithDisplayValue,
    type QueryTableVersions,
} from "@glide/computation-model-types";
import { type ActiveQueryBase, QueryState } from "./active-query";
import { type QueryIDsWithTableVersions, getFeatureSetting } from "@glide/common-core";
import { checkQueryTableVersions } from "@glide/backend-api";
import { mapRecordFilterUndefined } from "@glideapps/ts-necessities";
import type { ActionAppFacilities } from "@glide/common-core/dist/js/components/types";
import { Periodically } from "@glide/support";

// Cap on how many times the freshener retries a query that keeps failing, so a
// permanently-errored query doesn't re-issue `executeQuery` forever.
export const maxErroredQueryRetries = 3;

// Errors that won't fix themselves on retry, so we don't retry them at all: a
// malformed query (400, see `ErrorInfo(400, …)` in query-row-data-store) or a
// missing table (404).
const permanentErrorStatuses = new Set<number>([400, 404]);

function isRetriableQueryError(query: ActiveQueryBase): boolean {
    if (query.error === undefined) return false;
    if (permanentErrorStatuses.has(query.error.status)) return false;
    return query.errorRetryCount < maxErroredQueryRetries;
}

export type ListQueries = () => IterableIterator<ActiveQueryBase>;
type QueryChangedHandler = () => void;

export interface QueryRefresher {
    tableVersions(): QueryIDsWithTableVersions;
    // The IDs of active queries that finished in a retriable error state. These
    // have no recorded table versions, so they're invisible to the
    // version-based refresh check; the freshener retries them (bounded by
    // `maxErroredQueryRetries`, skipping permanent errors) so a query that
    // errored transiently (e.g. before the user was authenticated on first
    // login) can recover without a full page reload.
    retriableErroredQueryIDs(): Set<string>;
    // Whether this refresher actually re-runs errored queries. Only the
    // periodic `GbtQueryFreshener` does; a `NoOpQueryRefresher` never calls
    // `requery`, so callers must not surface a query for retry (and rely on it
    // recovering) when this is `false` — see `query-row-data-store`'s 403
    // handling, which would otherwise leave a 403 stuck as a permanent error.
    canRetryErroredQueries(): boolean;
    requery(queryIDs: Set<string>): void;
    onAfterQuery(query: ActiveQueryBase): void;
}

export class NoOpQueryRefresher implements QueryRefresher {
    static build(_queries: ListQueries, _handler: QueryChangedHandler): QueryRefresher {
        return new NoOpQueryRefresher();
    }

    tableVersions(): QueryIDsWithTableVersions {
        return {};
    }

    retriableErroredQueryIDs(): Set<string> {
        return new Set();
    }

    canRetryErroredQueries(): boolean {
        return false;
    }

    requery(_queryIDs: Set<string>) {
        return;
    }

    onAfterQuery(_query: ActiveQueryBase) {
        return;
    }
}

export class GbtQueryRefresher implements QueryRefresher {
    static build(queries: ListQueries, handler: QueryChangedHandler): QueryRefresher {
        return new GbtQueryRefresher(queries, handler);
    }

    constructor(readonly queries: ListQueries, readonly handler: QueryChangedHandler) {}

    tableVersions(): QueryIDsWithTableVersions {
        const tableVersions: QueryIDsWithTableVersions = {};
        for (const active of this.queries()) {
            if (!isRelevantQuery(active)) continue;
            if (!hasTableVersions(active)) continue;
            tableVersions[active.subQueryID] = active.tableVersions;
        }

        return tableVersions;
    }

    retriableErroredQueryIDs(): Set<string> {
        const ids = new Set<string>();
        for (const active of this.queries()) {
            if (!isRelevantQuery(active)) continue;
            if (!isRetriableQueryError(active)) continue;
            ids.add(active.subQueryID);
        }
        return ids;
    }

    canRetryErroredQueries(): boolean {
        return true;
    }

    requery(queryIDs: Set<string>) {
        let requeried = false;
        for (const active of this.queries()) {
            if (!isRelevantQuery(active)) continue;
            if (!queryIDs.has(active.subQueryID)) continue;

            requery(active);
            requeried = true;
        }

        if (!requeried) return;
        this.handler();
    }

    onAfterQuery(query: ActiveQueryBase) {
        const { cause, ...subQuery } = query.subQuery;
        query.subQuery = subQuery;
    }
}

function isRelevantQuery(query: ActiveQueryBase): boolean {
    if (query.state !== QueryState.Done) return false;
    if (query.callbacks.size === 0) return false;
    return true;
}

function hasTableVersions(query: ActiveQueryBase): query is ActiveQueryBase & { tableVersions: QueryTableVersions } {
    return query.tableVersions !== undefined;
}

function requery(query: ActiveQueryBase) {
    // Count retries of an errored query so `isRetriableQueryError` can cap them.
    // Version-driven requeries have no error and don't touch this counter; a
    // successful response resets it to 0 (see query-row-data-store).
    if (query.error !== undefined) {
        query.errorRetryCount++;
    }
    query.state = QueryState.Execute;
    query.subQuery = { ...query.subQuery, cause: "requery" };
    if (!isLoadingValue(query.currentValue)) {
        query.lastValue = query.currentValue;
        // This will be overwritten with a pure loading value by
        // ##gbtFixRefreshDeletes if we're completely reloading the query, as
        // opposed to incrementally.
        query.currentValue = makeLoadingValueWithDisplayValue(query.currentValue);
    }
}

export type BuildQueryRefresher = (queries: ListQueries, handler: QueryChangedHandler) => QueryRefresher;

export class RunningQueryFreshener {
    constructor(readonly buildQueryRefresher: BuildQueryRefresher, readonly stop: () => void) {}
}

export interface AppDetails {
    readonly appID: string;
    readonly appFacilities: ActionAppFacilities;
}

export type GetQueries = () => IterableIterator<QueryRefresher | undefined>;
export type GetAppDetails = () => AppDetails;
export type StartQueryFreshener = (
    interval: number,
    queries: GetQueries,
    getAppDetails: GetAppDetails
) => RunningQueryFreshener;

export class NoOpQueryFreshener {
    static start(_interval: number, _queries: GetQueries, _getAppDetails: GetAppDetails): RunningQueryFreshener {
        return new RunningQueryFreshener(NoOpQueryRefresher.build, () => {
            return;
        });
    }
}

interface GbtQueryDependencies {
    readonly check: typeof checkQueryTableVersions;
}

function checkQueryUpdates(queryTableVersions: QueryIDsWithTableVersions, changedTableIds: string[]) {
    const queryResults: Record<string, { didChange: boolean }> = {};

    for (const [queryId, tableVersions] of Object.entries(queryTableVersions)) {
        let didChange = false;
        for (const tableId of Object.keys(tableVersions)) {
            if (changedTableIds.includes(tableId)) {
                didChange = true;
                break;
            }
        }
        queryResults[queryId] = { didChange };
    }

    return queryResults;
}

export class GbtQueryFreshener {
    static start(interval: number, queries: GetQueries, getAppDetails: GetAppDetails): RunningQueryFreshener {
        const freshener = new GbtQueryFreshener(queries, getAppDetails);
        const stop = Periodically.run(interval, () => freshener.reload());
        return new RunningQueryFreshener(GbtQueryRefresher.build, stop);
    }

    check: GbtQueryDependencies["check"];

    constructor(
        readonly queries: GetQueries,
        readonly getAppDetails: GetAppDetails,
        { check = checkQueryTableVersions }: Partial<GbtQueryDependencies> = {}
    ) {
        this.check = check;
    }

    async reload() {
        const env = this.getAppDetails();
        if (env === undefined) return;

        const retryErroredQueries = getFeatureSetting("gbtRetryErroredQueries");

        const allTableVersions: QueryIDsWithTableVersions = {};
        const tableVersions: QueryTableVersions = {};
        const erroredQueryIDs = new Set<string>();

        for (const refresher of this.queries()) {
            if (refresher === undefined) continue;
            const refresherTableVersions = refresher.tableVersions();
            Object.assign(allTableVersions, refresherTableVersions);

            for (const [_, queryTableVersions] of Object.entries(refresherTableVersions)) {
                for (const [tableID, version] of Object.entries(queryTableVersions)) {
                    tableVersions[tableID] = Math.min(tableVersions[tableID] ?? Infinity, version);
                }
            }

            if (retryErroredQueries) {
                for (const queryID of refresher.retriableErroredQueryIDs()) {
                    erroredQueryIDs.add(queryID);
                }
            }
        }

        // A query that errored has no recorded table versions, so it never
        // appears in the version-change check below. Retry those (bounded by a
        // per-query attempt cap) so a transient error (e.g. a first-login query
        // that raced ahead of authentication) recovers without a full page
        // reload.
        const queryIDsToRequery = new Set(erroredQueryIDs);

        if (Object.keys(allTableVersions).length > 0) {
            const response = await this.check(env.appID, tableVersions, env.appFacilities);
            const changedTablesResults = checkQueryUpdates(allTableVersions, response.changedTableIds);
            const changedQueryResults: Record<string, true> = mapRecordFilterUndefined(
                changedTablesResults,
                r => r.didChange || undefined
            );

            for (const queryID of Object.keys(changedQueryResults)) {
                queryIDsToRequery.add(queryID);
            }
        }

        if (queryIDsToRequery.size === 0) return;

        for (const refresher of this.queries()) {
            if (refresher === undefined) continue;
            refresher.requery(queryIDsToRequery);
        }
    }
}
